// START IMPORTS
import CheckIcon from '@mui/icons-material/Check';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PhoneIcon from '@mui/icons-material/Phone';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import {
  getActivityConfig,
  getDncConfig,
  getInitials,
  getLeakedConfig,
  hasValue,
} from '../utils';

import {
  CardContainer,
  HeaderRow,
  HeaderTitle,
  PhoneNumberRow,
  PhoneNumber,
  FraudBadge,
  SectionDivider,
  StatusRow,
  StatusLabel,
  StatusItem,
  StatusIconWrapper,
  SectionHeader,
  SectionTitle,
  DetailGrid,
  DetailLabel,
  DetailValue,
  OwnerRow,
  OwnerInfo,
  OwnerAvatar,
  OwnerName,
} from './components';

import type { PhoneReportData } from 'components/DeepSearch/types';

// END IMPORTS

// START INTERFACES_AND_TYPES
interface PhoneReportPreviewCardProps {
  data: PhoneReportData;
}
// END INTERFACES_AND_TYPES

export const PhoneReportPreviewCard = ({
  data,
}: PhoneReportPreviewCardProps) => {
  // START DERIVED_DATA_AND_UTILS
  const leakedValue = data.leakedNumber?.trim() ?? '';
  const activityValue = data.userActivity?.trim() ?? '';
  const dncValue = data.dncStatus?.trim() ?? '';

  const leaked = leakedValue ? getLeakedConfig(leakedValue) : null;
  const activity = activityValue ? getActivityConfig(activityValue) : null;
  const dnc = dncValue ? getDncConfig(dncValue) : null;

  const hasStatusItems = Boolean(leakedValue || activityValue || dncValue);
  const hasLineDetails =
    hasValue(data.carrier) ||
    hasValue(data.prepaid) ||
    hasValue(data.lineType) ||
    hasValue(data.validNumber) ||
    hasValue(data.location) ||
    hasValue(data.zipCode) ||
    hasValue(data.timeZone);
  const hasOwner = data.ownersCount > 0 && Boolean(data.ownerName?.trim());
  // END DERIVED_DATA_AND_UTILS

  // START RENDER
  return (
    <CardContainer>
      <HeaderRow>
        <PhoneIcon fontSize="small" />
        <HeaderTitle>Phone Report</HeaderTitle>
      </HeaderRow>

      <PhoneNumberRow>
        <PhoneNumber>{data.phoneNumber}</PhoneNumber>
        <FraudBadge severity={data.fraudSeverity ?? 'low'}>
          <WarningAmberIcon sx={{ fontSize: 16 }} />
          {data.fraudRisk}
        </FraudBadge>
      </PhoneNumberRow>

      {hasStatusItems && (
        <>
          <SectionDivider />
          <StatusRow>
            <StatusLabel>Status:</StatusLabel>
            {leaked && (
              <StatusItem>
                <StatusIconWrapper severity={leaked.severity}>
                  {leaked.isWarning ? (
                    <WarningAmberIcon sx={{ fontSize: 14 }} color="inherit" />
                  ) : (
                    <CheckIcon sx={{ fontSize: 14 }} color="inherit" />
                  )}
                </StatusIconWrapper>
                Leaked Number: {leakedValue}
              </StatusItem>
            )}
            {activity && (
              <StatusItem>
                <StatusIconWrapper severity={activity.severity}>
                  {activity.isWarning ? (
                    <WarningAmberIcon sx={{ fontSize: 14 }} color="inherit" />
                  ) : (
                    <CheckIcon sx={{ fontSize: 14 }} color="inherit" />
                  )}
                </StatusIconWrapper>
                User Activity: {activityValue}
              </StatusItem>
            )}
            {dnc && (
              <StatusItem>
                <StatusIconWrapper severity={dnc.severity}>
                  {dnc.isWarning ? (
                    <WarningAmberIcon sx={{ fontSize: 14 }} color="inherit" />
                  ) : (
                    <CheckIcon sx={{ fontSize: 14 }} color="inherit" />
                  )}
                </StatusIconWrapper>
                {dncValue}
              </StatusItem>
            )}
          </StatusRow>
        </>
      )}

      {hasLineDetails && (
        <>
          <SectionDivider />
          <SectionHeader>
            <PhoneIcon sx={{ fontSize: 18 }} />
            <SectionTitle>Line Details</SectionTitle>
          </SectionHeader>
          <DetailGrid>
            {hasValue(data.carrier) && (
              <>
                <DetailLabel>Carrier</DetailLabel>
                <DetailValue>{data.carrier}</DetailValue>
              </>
            )}
            {hasValue(data.prepaid) && (
              <>
                <DetailLabel>Prepaid</DetailLabel>
                <DetailValue>{data.prepaid}</DetailValue>
              </>
            )}
            {hasValue(data.lineType) && (
              <>
                <DetailLabel>Line Type</DetailLabel>
                <DetailValue>{data.lineType}</DetailValue>
              </>
            )}
            {hasValue(data.validNumber) && (
              <>
                <DetailLabel>Valid Number</DetailLabel>
                <DetailValue>{data.validNumber}</DetailValue>
              </>
            )}
            {hasValue(data.location) && (
              <>
                <DetailLabel>Location</DetailLabel>
                <DetailValue>{data.location}</DetailValue>
              </>
            )}
            {hasValue(data.zipCode) && (
              <>
                <DetailLabel>Zip Code</DetailLabel>
                <DetailValue>{data.zipCode}</DetailValue>
              </>
            )}
            {hasValue(data.timeZone) && (
              <>
                <DetailLabel>Time Zone</DetailLabel>
                <DetailValue>{data.timeZone}</DetailValue>
              </>
            )}
          </DetailGrid>
        </>
      )}

      {hasOwner && (
        <>
          <SectionDivider />
          <SectionHeader>
            <PersonOutlineIcon sx={{ fontSize: 18 }} />
            <SectionTitle>
              {data.ownersCount} Potential Owner
              {data.ownersCount !== 1 ? 's' : ''} Identified
            </SectionTitle>
          </SectionHeader>
          <OwnerRow>
            <OwnerInfo>
              <OwnerAvatar>{getInitials(data.ownerName)}</OwnerAvatar>
              <OwnerName>{data.ownerName}</OwnerName>
            </OwnerInfo>
          </OwnerRow>
        </>
      )}
    </CardContainer>
  );
  // END RENDER
};
